import Auth from '@aws-amplify/auth';
import { HttpError } from 'react-admin';

export const ADMIN_FULL_ACCESS = 'ADMIN_FULL_ACCESS';
export const ADMIN_SUPPORT_READ_ONLY = 'ADMIN_SUPPORT_READ_ONLY';
export const ADMIN_SUPPORT = 'ADMIN_SUPPORT';
export const ADMIN_BILLING_MANAGER = 'ADMIN_BILLING_MANAGER';
export const ADMIN_FRAUD_SUPPORT = 'ADMIN_FRAUD_SUPPORT';
export const ACCESS_CHANGED = 'rollo-admin-access-changed';

export type AdminAccess = {
  canRead: boolean;
  // canWrite remains the Admin-only permission for unclassified/global actions.
  canWrite: boolean;
  canReadBilling: boolean;
  canManageBilling: boolean;
  canReadFraud: boolean;
  canManageFraud: boolean;
  canManageUsers: boolean;
  roles: string[];
};

export type AdminPermission = Exclude<keyof AdminAccess, 'roles'>;

type Session = ReturnType<typeof Auth.currentSession> extends Promise<infer T> ? T : never;

export type AdminAccessState = {
  status: 'loading' | 'ready' | 'unavailable';
  access: AdminAccess;
};

export type SessionAccess = {
  status: 'ready';
  session: Session;
  access: AdminAccess;
  generation: number;
};

export type AccessLoadResult =
  | SessionAccess
  | { status: 'unavailable'; error: unknown }
  | { status: 'superseded' };

type IdTokenClaims = {
  token_use?: unknown;
  exp?: unknown;
  'cognito:groups'?: unknown;
};

const ROLE_NAMES = {
  [ADMIN_FULL_ACCESS]: 'Admin',
  [ADMIN_SUPPORT_READ_ONLY]: 'Read-only',
  [ADMIN_SUPPORT]: 'Support',
  [ADMIN_BILLING_MANAGER]: 'Billing Manager',
  [ADMIN_FRAUD_SUPPORT]: 'Fraud support',
};

export const NO_ACCESS: AdminAccess = {
  canRead: false,
  canWrite: false,
  canReadBilling: false,
  canManageBilling: false,
  canReadFraud: false,
  canManageFraud: false,
  canManageUsers: false,
  roles: [],
};

let currentState: AdminAccessState = { status: 'loading', access: NO_ACCESS };
let generation = 0;
let suspended = false;
let pendingAccess: Promise<AccessLoadResult> | null = null;
let currentSessionAccess: SessionAccess | null = null;

const publishAccess = (status: AdminAccessState['status'], next: AdminAccess) => {
  const access =
    JSON.stringify(currentState.access) === JSON.stringify(next) ? currentState.access : next;
  if (currentState.status !== status || currentState.access !== access) {
    currentState = { status, access };
    window.dispatchEvent(new Event(ACCESS_CHANGED));
  }
};

const invalidateAccess = (status: AdminAccessState['status']) => {
  generation += 1;
  pendingAccess = null;
  currentSessionAccess = null;
  publishAccess(status, NO_ACCESS);
};

export const resetAccess = () => {
  suspended = true;
  invalidateAccess('unavailable');
};

export const beginAccessSession = () => {
  suspended = false;
  invalidateAccess('loading');
};

export const getAccessState = () => currentState;

const isSessionValid = (session: Session) => {
  const token = session.getIdToken();
  const payload: IdTokenClaims = token.decodePayload();
  return (
    Boolean(token.getJwtToken()) &&
    payload.token_use === 'id' &&
    typeof payload.exp === 'number' &&
    Number.isFinite(payload.exp) &&
    payload.exp * 1000 > Date.now()
  );
};

// Uses the same refreshed ID token as API requests. Client checks are UX only;
// verified Cognito claims and method security on the API enforce authorization.
export const getSessionAccess = (session: Session): AdminAccess => {
  const token = session.getIdToken();
  const payload: IdTokenClaims = token.decodePayload();
  const groups = Array.isArray(payload['cognito:groups']) ? payload['cognito:groups'] : [];
  const valid = isSessionValid(session);
  const has = (group: string) => valid && groups.includes(group);
  const canWrite = has(ADMIN_FULL_ACCESS);
  const readOnly = has(ADMIN_SUPPORT_READ_ONLY);
  const support = has(ADMIN_SUPPORT);
  const billing = has(ADMIN_BILLING_MANAGER);
  const fraud = has(ADMIN_FRAUD_SUPPORT);
  const access: AdminAccess = {
    canRead: canWrite || readOnly || support || billing || fraud,
    canWrite,
    canReadBilling: canWrite || readOnly || billing || fraud,
    canManageBilling: canWrite || billing,
    canReadFraud: canWrite || readOnly || support || fraud,
    canManageFraud: canWrite || fraud,
    canManageUsers: canWrite || fraud,
    roles: Object.keys(ROLE_NAMES)
      .filter(has)
      .map((group) => ROLE_NAMES[group]),
  };

  return access;
};

export const loadAccess = (): Promise<AccessLoadResult> => {
  if (suspended) return Promise.resolve({ status: 'superseded' });
  if (pendingAccess) return pendingAccess;

  const operationGeneration = generation;
  const operation: Promise<AccessLoadResult> = Promise.resolve()
    .then(() => Auth.currentSession())
    .then((session): AccessLoadResult => {
      if (operationGeneration !== generation || suspended) {
        return { status: 'superseded' };
      }
      if (!isSessionValid(session)) {
        throw new HttpError('Session is unavailable. Please sign in again.', 401);
      }
      publishAccess('ready', getSessionAccess(session));
      const result: SessionAccess = {
        status: 'ready',
        session,
        access: currentState.access,
        generation: operationGeneration,
      };
      currentSessionAccess = result;
      return result;
    })
    .catch((error: unknown): AccessLoadResult => {
      if (operationGeneration !== generation || suspended) {
        return { status: 'superseded' };
      }
      currentSessionAccess = null;
      publishAccess('unavailable', NO_ACCESS);
      return { status: 'unavailable', error };
    })
    .finally(() => {
      if (pendingAccess === operation) pendingAccess = null;
    });
  pendingAccess = operation;
  return operation;
};

export const assertCurrentSessionAccess = (result: SessionAccess) => {
  if (
    suspended ||
    result.generation !== generation ||
    result !== currentSessionAccess ||
    !isSessionValid(result.session)
  ) {
    throw new HttpError('Session changed. Please try again.', 401);
  }
};

// Match paths relative to their configured API base, including version prefixes.
const relativePath = (url: URL, base?: string) => {
  if (!base) return null;
  const api = new URL(base, window.location.origin);
  const prefix = api.pathname.replace(/\/+$/, '');
  if (
    url.origin !== api.origin ||
    (url.pathname !== prefix && !url.pathname.startsWith(`${prefix}/`))
  ) {
    return null;
  }
  return decodeURIComponent(url.pathname.slice(prefix.length)).replace(/\/+$/, '');
};

export const assertRequestAccess = (
  access: AdminAccess,
  url: string,
  method = 'GET',
  apiBase?: string,
  devicesBase?: string
) => {
  const verb = method.toUpperCase();
  const parsed = new URL(url, window.location.origin);
  const path = relativePath(parsed, apiBase);
  const read = verb === 'GET' || verb === 'HEAD';
  let permission: AdminPermission = read ? 'canRead' : 'canWrite';

  if (path === null) {
    // The separate IoT API is read-only here. Never forward admin tokens to unknown URLs.
    if (relativePath(parsed, devicesBase) === null) {
      throw new HttpError('Access denied: unknown admin API.', 403);
    }
    if (!read) {
      throw new HttpError('Access denied: the device API is read-only.', 403);
    }
  } else if (path === '/users' && read) {
    const fraudFilters = [
      'filter.fraud',
      'filter.fraud.reason',
      'filter.blocked',
      'filter.blockedByAdmin',
      'filter.phone.verified',
    ];
    const fraudQuery =
      fraudFilters.some((key) => parsed.searchParams.has(key)) ||
      parsed.searchParams
        .getAll('sort')
        .some((sort) => sort.split(',').some((field) => field.startsWith('fraud')));
    if (fraudQuery) permission = 'canReadFraud';
  } else if (/^\/users\/[^/]+\/billing(?:\/|$)/.test(path)) {
    const embeddedRead = read && /^\/users\/[^/]+\/billing\/(balance|payment-fees)$/.test(path);
    const balanceCheck = verb === 'POST' && /\/ach\/balance-check$/.test(path);
    if (embeddedRead) {
      permission = 'canRead';
    } else if (read || balanceCheck) {
      permission = 'canReadBilling';
    } else {
      permission = 'canManageBilling';
    }
  } else if (/^\/(debtors|report|v1\/ach-tiers)(?:\/|$)/.test(path)) {
    if (read) {
      permission = 'canReadBilling';
    } else if (path.startsWith('/report')) {
      permission = 'canWrite';
    } else {
      permission = 'canManageBilling';
    }
  } else if (/^\/ach-risk-profiles(?:\/|$)/.test(path)) {
    const embeddedRead = read && /^\/ach-risk-profiles\/\d+$/.test(path);
    if (embeddedRead) {
      permission = 'canRead';
    } else if (read) {
      permission = 'canReadBilling';
    } else {
      permission = 'canManageBilling';
    }
  } else if (/^\/users\/[^/]+\/budget$/.test(path)) {
    permission = read ? 'canRead' : 'canManageBilling';
  } else if (/^\/users\/[^/]+\/block-charging$/.test(path)) {
    // Charging-status reads remain Admin-only; billing roles can change charging controls.
    permission = read ? 'canWrite' : 'canManageBilling';
  } else if (/^\/fraudsters(?:\/|$)/.test(path)) {
    const dictionaryRead = read && path === '/fraudsters/dictionary/fraud-types';
    if (dictionaryRead) {
      permission = 'canRead';
    } else if (read) {
      permission = 'canReadFraud';
    } else {
      permission = 'canManageFraud';
    }
  } else if (/^\/users\/blocklist\/phones(?:\/|$)/.test(path)) {
    permission = read ? 'canRead' : 'canManageFraud';
  } else if (
    !read &&
    (/^\/users\/(enable|disable|confirm|reset-password|email|block-shipping|mfa\/disable|mfa\/enable-sms)$/.test(
      path
    ) ||
      /^\/users\/[^/]+(?:\/(small-packages|expedited-shipments|kyc(?:\/complete|\/timer)?))?$/.test(
        path
      ))
  ) {
    permission = 'canManageUsers';
  } else if (verb === 'POST' && /^\/audit\/(HIGH|MEDIUM|LOW)\/query$/i.test(path)) {
    permission = 'canRead';
  }

  if (!access.canRead || !access[permission]) {
    throw new HttpError('Access denied: this account cannot perform this action.', 403);
  }
};
